import {
  Alert,
  Anchor,
  Box,
  Button,
  Container,
  Grid,
  Image,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { getCloudMetadata, getCurrentUser } from "../shared/getCurrentUser";
import { imgDinLabelLaptop } from "../shared/images";
import { Login } from "../shared/Login";
import { RestApi } from "../shared/RestApi";
import { Log } from "../shared/logging/Log";
import { NavBarContainer } from "./NavBarContainer";

declare const Stripe: (key: string) => {
  redirectToCheckout: (opts: {
    sessionId: string;
  }) => Promise<{ error?: { message: string } }>;
};

export function BuyPage() {
  const { t } = useTranslation();
  const [payError, setPayError] = React.useState<string | null>(null);
  const [paying, setPaying] = React.useState(false);
  const [togglingBought, setTogglingBought] = React.useState(false);
  const [signInOpen, setSignInOpen] = React.useState(false);
  const user = getCurrentUser();
  const metadata = getCloudMetadata();
  const alreadyBought = Boolean(user?.Bought);

  // Browser back from Stripe often restores this page from bfcache with
  // paying still true (redirect navigates away before finally runs).
  React.useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setPaying(false);
        setPayError(null);
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const onPay = async () => {
    if (!user) {
      setSignInOpen(true);
      return;
    }
    if (alreadyBought) return;

    Log.logger.logReportData("BuyPagePayClicked", {
      alreadyBought,
    });

    setPaying(true);
    setPayError(null);
    try {
      const session = await RestApi.createBuySession();
      if (session.bought) {
        window.location.reload();
        return;
      }
      const stripe = Stripe(metadata.StripePublishableKey);
      const result = await stripe.redirectToCheckout({
        sessionId: session.sessionId,
      });
      if (result.error) {
        setPayError(result.error.message);
      }
    } catch {
      setPayError(t("buyPage.payError"));
    } finally {
      setPaying(false);
    }
  };

  const onToggleBought = async () => {
    setTogglingBought(true);
    setPayError(null);
    try {
      await RestApi.toggleBought();
      window.location.reload();
    } catch {
      setPayError(t("buyPage.toggleBoughtError"));
      setTogglingBought(false);
    }
  };

  return (
    <NavBarContainer fullBleed showFooter>
      <Box
        style={{
          background:
            "linear-gradient(160deg, #fff8f2 0%, #ffffff 45%, #fff4eb 100%)",
          minHeight: "calc(100vh - 60px - 160px)",
        }}
        py={{ base: "xl", md: 56 }}
      >
        <Container size="lg">
          <Grid align="center">
            <Grid.Col span={{ base: 12, md: 5 }} visibleFrom="md">
              <Image
                src={imgDinLabelLaptop}
                alt="DinLabel"
                maw={420}
                mx="auto"
                style={{
                  filter: "drop-shadow(0 20px 40px rgba(44, 45, 48, 0.16))",
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 7 }}>
              <Stack gap="lg" maw={480} mx={{ base: "auto", md: 0 }}>
                <div>
                  <Title order={1} c="charcoal.8" mb="xs">
                    {t("buyPage.title")}
                  </Title>
                  <Text c="dimmed" size="lg">
                    {t("buyPage.intro")}
                  </Text>
                </div>

                {payError && (
                  <Alert color="red" variant="light" title={t("buyPage.errorTitle")}>
                    {payError}
                  </Alert>
                )}

                {user?.IsAdmin && (
                  <Stack gap="sm">
                    {!alreadyBought && (
                      <Alert color="orange" variant="light">
                        {t("buyPage.adminTestMode")}
                      </Alert>
                    )}
                    <Button
                      variant="outline"
                      color="orange"
                      size="xs"
                      loading={togglingBought}
                      onClick={() => void onToggleBought()}
                    >
                      {t("buyPage.debugToggleBought")}
                    </Button>
                  </Stack>
                )}

                {alreadyBought ? (
                  <Box
                    p="xl"
                    style={{
                      background: "white",
                      border: "1px solid var(--mantine-color-gray-2)",
                      borderRadius: "var(--mantine-radius-md)",
                      boxShadow: "0 8px 32px rgba(44, 45, 48, 0.06)",
                    }}
                  >
                    <Stack gap="md">
                      <Title order={3} c="brand.7">
                        {t("buyPage.thanks")}
                      </Title>
                      <Anchor component={Link} to="/install" c="brand.7" fw={600}>
                        {t("buyPage.goToDownload")}
                      </Anchor>
                      <Anchor component={Link} to="/userinfo" c="brand.7" fw={600}>
                        {t("buyPage.checkAccount")}
                      </Anchor>
                    </Stack>
                  </Box>
                ) : (
                  <Box
                    p="xl"
                    style={{
                      background: "white",
                      border: "1px solid var(--mantine-color-gray-2)",
                      borderRadius: "var(--mantine-radius-md)",
                      boxShadow: "0 8px 32px rgba(44, 45, 48, 0.06)",
                    }}
                  >
                    <Stack gap="lg">
                      <div>
                        <Text fw={600} size="lg" c="charcoal.8" mb={4}>
                          {t("buyPage.panelTitle")}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {t("buyPage.price", {
                            price: metadata.Price.toFixed(2),
                          })}
                        </Text>
                      </div>

                      <Button
                        onClick={onPay}
                        loading={paying}
                        size="lg"
                        color="brand"
                        fullWidth
                      >
                        {t("buyPage.payWithCard")}
                      </Button>

                      <Stack gap="xs">
                        <Text size="sm" c="dimmed">
                          {t("buyPage.orVia")}
                        </Text>
                        <Anchor
                          component={Link}
                          to="/install"
                          size="sm"
                          c="charcoal.5"
                          underline="hover"
                        >
                          {t("buyPage.downloadDemo")}
                        </Anchor>
                        <Anchor
                          component={Link}
                          to="/viewdistributors"
                          size="sm"
                          c="charcoal.5"
                          underline="hover"
                        >
                          {t("buyPage.contactDistributor")}
                        </Anchor>
                        <Anchor
                          component={Link}
                          to="/distributorportal"
                          size="sm"
                          c="charcoal.5"
                          underline="hover"
                        >
                          {t("buyPage.becomeDistributor")}
                        </Anchor>
                      </Stack>
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      <Modal opened={signInOpen} onClose={() => setSignInOpen(false)} centered>
        <Login returnTo="/buy" />
      </Modal>
    </NavBarContainer>
  );
}
